import { useAtom } from 'jotai';

import { classModuleFiltersAtom } from '../store/atoms/classModuleFiltersAtom';
import { type ClassModuleFilters, INITIAL_CLASS_MODULE_FILTERS } from '../types/classModuleFilters';

/**
 * Hook para gerenciar os filtros da listagem de módulos.
 *
 * @returns Filtros atuais e funções para atualizar e resetar.
 *
 * @example
 * ```tsx
 * const { filters, updateFilters, resetFilters } = useClassModuleFilters();
 * updateFilters({ description: 'Gestão', page: 1 });
 * ```
 */
export function useClassModuleFilters() {
  const [filters, setFilters] = useAtom(classModuleFiltersAtom);

  /**
   * Atualiza parcialmente os filtros. Reseta para a primeira página, exceto
   * quando o próprio `page` é fornecido no `partial` (navegação de páginas).
   */
  const updateFilters = (partial: Partial<ClassModuleFilters>) => {
    setFilters(prev => {
      const resetPage = 'page' in partial ? {} : { page: 1 };
      return { ...prev, ...resetPage, ...partial };
    });
  };

  /** Limpa todos os filtros e volta para a primeira página. */
  const resetFilters = () => {
    setFilters(INITIAL_CLASS_MODULE_FILTERS);
  };

  return { filters, updateFilters, resetFilters } as const;
}
